import { ASSET_BETA, COOKIE_REVISION_KEY } from 'constants/global';
import { isAssetMismatch, isNewRevisionAvailable, isPublicRevision } from 'factories/revision';
import Cookies from 'js-cookie';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';

export const readCookieRevision = () => Cookies.get(COOKIE_REVISION_KEY);
const readGlobalState = (state: IState) => state.global;
const readRequestedAssetRevision = (state: IState) => state.global.requestedAssetRevision;

export const getOverrideRevisionCookie = (state: IState) =>
  state.development.simulatedRevisionCookie;
const getCookieRevision = createSelector(
  [readCookieRevision, getOverrideRevisionCookie],
  (cookieRevision, simulatedRevision) => simulatedRevision ?? cookieRevision
);

export const getGlobalState = readGlobalState;

export const getInitializedAssetRevision = createSelector(
  [getGlobalState],
  (globalState): string | null => globalState.initializedAssetRevision
);

export const getDatePickerDropdownIsRangeMode = createSelector(
  [getGlobalState],
  (globalState): boolean => globalState.datePickerDropdown.isRange
);

export const getClientReportDatePickerIsRangeMode = createSelector(
  [getGlobalState],
  (globalState): boolean => globalState.clientReportDatePickerDropdown.isRange
);

export const getAvailableAssetRevision = createSelector(
  [getGlobalState],
  (globalState): string | null => globalState.availableAssetRevision
);

export const getAssetRevisionStatus = createSelector(
  [
    getInitializedAssetRevision,
    getAvailableAssetRevision,
    readRequestedAssetRevision,
    getCookieRevision,
  ],
  (initializedAssetRevision, availableAssetRevision, requestedAssetRevision, cookieRevision) => {
    // The cookie (will) always be current, beta or commit hash when customized.
    const activeRevision = cookieRevision || initializedAssetRevision || null;
    const isPublic = isPublicRevision(activeRevision);
    const assetsNotFound = isAssetMismatch({ requestedAssetRevision, initializedAssetRevision });
    const newRevisionAvailable = isNewRevisionAvailable({
      availableAssetRevision,
      initializedAssetRevision,
      isPublic,
    });

    return {
      isPublic,
      isBeta: activeRevision === ASSET_BETA,
      isCustomRevisionActive: cookieRevision === initializedAssetRevision,
      assetsNotFound,
      revision: initializedAssetRevision,
      newRevisionAvailable,
    };
  }
);

export const getIsAppInitialized = createSelector([getGlobalState], (globalState): boolean => {
  return globalState.initializedAt !== null;
});

export const getShowKeyboardShortcutModal = createSelector(
  [getGlobalState],
  (globalState): boolean => {
    return globalState.showKeyboardShortcutModal;
  }
);

export const getShowGlobalSearch = createSelector(
  [getGlobalState],
  (globalState): boolean => globalState.showGlobalSearch
);

export const getDetectedTimezone = createSelector([getGlobalState], (globalState): string => {
  return globalState.detectedTimezone;
});
